// ============================================================================
// Visual mapping for session types → Pulse accents. Centralised so the calendar
// events, lists, and legends all agree on colour language.
// ============================================================================
import type { EntryProvenance, SessionType } from "./viewModel";
import type { BadgeTone } from "../components";

export interface TypeStyle {
  /** Tailwind gradient bg utility for solid event chips. */
  grad: string;
  /** Soft tint bg for low-emphasis surfaces. */
  soft: string;
  /** Text colour on soft surfaces. */
  text: string;
  /** Dot / accent colour utility. */
  dot: string;
  /** Badge tone for the Pulse <Badge> component. */
  tone: BadgeTone;
  label: string;
}

export const SESSION_STYLE: Record<SessionType, TypeStyle> = {
  strength: { grad: "bg-grad-coral", soft: "bg-coral-soft", text: "text-volt-600", dot: "bg-coral", tone: "volt", label: "Strength" },
  cardio: { grad: "bg-grad-ocean", soft: "bg-ocean-soft", text: "text-ocean-600", dot: "bg-ocean", tone: "info", label: "Cardio" },
  mobility: { grad: "bg-grad-violet", soft: "bg-violet-soft", text: "text-ember-600", dot: "bg-violet", tone: "ember", label: "Mobility" },
  hiit: { grad: "bg-grad-amber", soft: "bg-amber-soft", text: "text-amber-600", dot: "bg-amber", tone: "warning", label: "HIIT" },
  mixed: { grad: "bg-grad-lime", soft: "bg-lime-soft", text: "text-lime-600", dot: "bg-lime", tone: "success", label: "Mixed" },
};

// ============================================================================
// ENTRY_STYLE — PROVENANCE → colour, the calendar-merge palette (spec §1/§5).
// ----------------------------------------------------------------------------
// PT sessions keep their TYPE colours via SESSION_STYLE; activities (and the
// other merged sources) colour by WHERE they came from, not what kind they are:
//   • pt_session         → coral  (bg-grad-coral) — billable PT
//   • activity_suggested → violet (bg-grad-violet, ember accent) — coach homework
//   • activity_own       → lime   (bg-grad-lime) — the client's own exercise
//   • meal               → amber  (bg-grad-amber) — a day's meal summary marker
//   • weekly_goal        → lime   (bg-grad-lime) — the week-level coach goal banner
// Shared by the merged calendar grids and the bilingual CalendarLegend so the
// trainer and client see the same colour language.
// ============================================================================
export const ENTRY_STYLE: Record<EntryProvenance, TypeStyle> = {
  pt_session: { grad: "bg-grad-coral", soft: "bg-coral-soft", text: "text-volt-600", dot: "bg-coral", tone: "volt", label: "Paid PT session" },
  activity_suggested: { grad: "bg-grad-violet", soft: "bg-violet-soft", text: "text-ember-600", dot: "bg-violet", tone: "ember", label: "Coach homework" },
  activity_own: { grad: "bg-grad-lime", soft: "bg-lime-soft", text: "text-lime-600", dot: "bg-lime", tone: "success", label: "Your own exercise" },
  meal: { grad: "bg-grad-amber", soft: "bg-amber-soft", text: "text-amber-600", dot: "bg-amber", tone: "warning", label: "Meals" },
  weekly_goal: { grad: "bg-grad-lime", soft: "bg-lime-soft", text: "text-lime-600", dot: "bg-lime", tone: "success", label: "Weekly goal" },
};
